/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import { createRouter, createWebHistory } from "vue-router";
import type { RouteLocationNormalized, NavigationGuardNext } from "vue-router";
import { setupLayouts } from "virtual:generated-layouts";
import { routes } from "vue-router/auto-routes";
import { useAuthStore } from "@/stores/authStore";
import { useMaintenanceStore } from "@/stores/maintenanceStore";
import { resolveMaintenanceRedirect } from "@/router/maintenanceGuard";
import { isBetLimitPath } from "@/constants/featureFlags";
import { useFeatureFlagStore } from "@/stores/featureFlagStore";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: setupLayouts(routes),
});

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err: Error, to: RouteLocationNormalized) => {
  if (err?.message?.includes?.("Failed to fetch dynamically imported module")) {
    if (!localStorage.getItem("vuetify:dynamic-reload")) {
      console.log("Reloading page to fix dynamic import error");
      localStorage.setItem("vuetify:dynamic-reload", "true");
      location.assign(to.fullPath);
    } else {
      console.error("Dynamic import error, reloading page did not fix it", err);
    }
  } else {
    console.error(err);
  }
});

router.isReady().then(() => {
  localStorage.removeItem("vuetify:dynamic-reload");
});

// Helper function to check route authorization
function checkRouteAuthorization(
  authStore: ReturnType<typeof useAuthStore>,
  checkPermission: () => boolean,
  errorMessage: string
): boolean {
  // Check if user has access token
  const token = authStore.getAccessToken();
  if (!token) {
    return false;
  }

  try {
    // Check specific permission
    return checkPermission();
  } catch (err) {
    console.error(errorMessage, err);
    return false;
  }
}

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const publicPages = ["/login", "/maintenance"];
  const adminOnlyPages = ["/admin/extension", "/admin/bulk-extension", "/admin/create-user", "/admin/notification", "/admin/maintenance", "/admin/telegram-usage", "/admin/user-devices", "/admin/feature-users", "/admin/campaign", "/admin/parser-analytics", "/admin/stats/bet-limit"];
  const authStore = useAuthStore();

  // Control menu visibility based on route
  // Hide menu on public pages (login, maintenance)
  // Show menu on authenticated pages if user has token
  if (publicPages.includes(to.path)) {
    authStore.setShowAppBarMenu(false);
  } else if (authStore.getAccessToken()) {
    authStore.setShowAppBarMenu(true);
  }

  // Maintenance check. The store is kept fresh by the polling started in
  // plugins/maintenance.ts, so this guard only reads the current answer and
  // never blocks a navigation on a network round trip.
  const maintenanceRedirect = resolveMaintenanceRedirect({
    path: to.path,
    isMaintenanceMode: useMaintenanceStore().isMaintenanceMode,
    isAdmin: authStore.isAdmin,
  });
  if (maintenanceRedirect) {
    next(maintenanceRedirect);
    return;
  }

  const authRequired = !publicPages.includes(to.path);
  // Use authStore instead of direct localStorage access
  const loggedIn = authStore.userData;

  if (authRequired && !loggedIn) {
    next("/login");
    return;
  }

  // Check admin permission for admin-only routes
  if (adminOnlyPages.includes(to.path)) {
    const isAuthorized = checkRouteAuthorization(
      authStore,
      () => authStore.isAdmin,
      "Error checking admin permission:"
    );

    if (!isAuthorized) {
      next("/");
      return;
    }
  }

  // Dư chuẩn is shown to admins and to users granted it on /admin/feature-users.
  if (isBetLimitPath(to.path) && !useFeatureFlagStore().canSeeBetLimit) {
    next("/");
    return;
  }

  // Redirect old calculate page to new calculate-v2
  if (to.path === "/calculate") {
    next("/calculate-v2");
    return;
  }

  next();
});

// router.afterEach(() => {
//   const authStore = useAuthStore(); // Assuming you have an auth store
//   const userId = authStore.username; // Replace with your actual user ID logic
//   if (userId) {
//     set({
//       page_path: window.location.pathname,
//       user_id: userId, // Set the user ID here
//     });
//   }
// });

export default router;
